import { useState, useRef, useEffect, useCallback } from 'react'
import { LiveAvatarSession, SessionState, SessionEvent, AgentEventsEnum, VoiceChatEvent, VoiceChatState } from '@heygen/liveavatar-web-sdk'
import { chatApi } from '../api'

const useLiveAvatar = ({ language, onMessage }: { language: string; onMessage?: (msg: { role: 'user' | 'assistant'; content: string }) => void }) => {
  const [isLoadingSession, setIsLoadingSession] = useState(false)
  const [isLoadingRepeat, setIsLoadingRepeat] = useState(false)
  const [stream, setStream] = useState<MediaStream>()
  const [isUserTalking, setIsUserTalking] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [userMediaStream, setUserMediaStream] = useState<MediaStream | null>(null)

  const sessionRef = useRef<LiveAvatarSession | null>(null)
  const mediaStream = useRef<HTMLVideoElement>(null)
  const onMessageRef = useRef(onMessage)

  useEffect(() => {
    onMessageRef.current = onMessage
  }, [onMessage])

  const startSession = useCallback(async ({ instructions, avatarName, meteredSessionId }: { instructions?: string; avatarName?: string; meteredSessionId?: number | null } = {}) => {
    console.remote('useLiveAvatar: startSession called')

    // Guard: prevent double-start
    if (sessionRef.current) {
      console.remote('useLiveAvatar: session already started, skipping')
      return
    }

    setIsLoadingSession(true)

    try {
      // Fetch fresh session token
      const { sessionToken } = await chatApi.getLiveAvatarToken({
        avatarId: avatarName || import.meta.env.VITE_HEYGEN_AVATAR_NAME,
        mode: 'FULL',
        instructions,
        meteredSessionId,
      })

      console.remote('useLiveAvatar: token obtained')

      // Create LiveAvatar session with voice chat enabled
      const session = new LiveAvatarSession(sessionToken, { voiceChat: true })

      // Register event listeners
      session.on(SessionEvent.SESSION_STATE_CHANGED, (state: SessionState) => {
        console.remote('useLiveAvatar: session state changed:', state)
        if (state === SessionState.CONNECTING) {
          setIsLoadingSession(true)
        }
      })

      session.on(SessionEvent.SESSION_STREAM_READY, async () => {
        console.remote('useLiveAvatar: STREAM READY')
        setIsLoadingSession(false)
        setStream(new MediaStream())

        // Start voice chat and listening AFTER stream is ready
        try {
          console.remote('useLiveAvatar: starting voiceChat.start()...')
          await session.voiceChat.start()
          console.remote('useLiveAvatar: voiceChat started, state:', session.voiceChat.state, 'isMuted:', session.voiceChat.isMuted)
          session.startListening()
          setIsListening(true)
          console.remote('useLiveAvatar: startListening() called')
        } catch (err) {
          console.remote('useLiveAvatar: ERROR starting voice chat:', err)
        }
      })

      session.on(SessionEvent.SESSION_CONNECTION_QUALITY_CHANGED, (quality: any) => {
        console.remote('useLiveAvatar: connection quality:', quality)
      })

      session.on(SessionEvent.SESSION_DISCONNECTED, (reason: any) => {
        console.remote('useLiveAvatar: SESSION DISCONNECTED, reason:', reason)
      })

      session.on(AgentEventsEnum.USER_SPEAK_STARTED, () => {
        console.remote('useLiveAvatar: USER_SPEAK_STARTED')
        setIsUserTalking(true)
      })

      session.on(AgentEventsEnum.USER_SPEAK_ENDED, () => {
        console.remote('useLiveAvatar: USER_SPEAK_ENDED')
        setIsUserTalking(false)
      })

      session.on(AgentEventsEnum.USER_TRANSCRIPTION, (event: any) => {
        console.remote('useLiveAvatar: USER_TRANSCRIPTION:', event.text || event)
        if (event.text?.trim() && onMessageRef.current) {
          onMessageRef.current({ role: 'user', content: event.text })
        }
      })

      session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
        console.remote('useLiveAvatar: AVATAR_SPEAK_STARTED')
      })

      session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
        console.remote('useLiveAvatar: AVATAR_SPEAK_ENDED')
      })

      session.on(AgentEventsEnum.AVATAR_TRANSCRIPTION, (event: any) => {
        console.remote('useLiveAvatar: AVATAR_TRANSCRIPTION:', event.text || event)
        if (event.text?.trim() && onMessageRef.current) {
          onMessageRef.current({ role: 'assistant', content: event.text })
        }
      })

      // Voice chat state events
      session.voiceChat.on(VoiceChatEvent.STATE_CHANGED, (state: VoiceChatState) => {
        console.remote('useLiveAvatar: voiceChat STATE_CHANGED:', state)
      })

      session.voiceChat.on(VoiceChatEvent.MUTED, () => {
        console.remote('useLiveAvatar: voiceChat MUTED')
      })

      session.voiceChat.on(VoiceChatEvent.UNMUTED, () => {
        console.remote('useLiveAvatar: voiceChat UNMUTED')
      })

      // Start the session
      console.remote('useLiveAvatar: calling session.start()...')
      await session.start()
      console.remote('useLiveAvatar: session.start() completed, state:', session.state)

      // Attach to video element if ready
      if (mediaStream.current) {
        console.remote('useLiveAvatar: attaching to video element')
        session.attach(mediaStream.current)
        mediaStream.current.play()
      } else {
        console.remote('useLiveAvatar: WARNING - no video element ref available for attach')
      }

      // Store session reference
      sessionRef.current = session
    } catch (error) {
      console.remote('useLiveAvatar: ERROR in startSession:', error)
      setIsLoadingSession(false)
      throw error
    }
  }, [])

  const endSession = useCallback(async () => {
    console.remote('useLiveAvatar: endSession called')

    // Stop listening first
    if (isListening && sessionRef.current) {
      try {
        sessionRef.current.stopListening()
        setIsListening(false)
      } catch (error) {
        console.remote('useLiveAvatar: error stopping listening:', error)
      }
    }

    // Clean up session
    if (sessionRef.current) {
      try {
        sessionRef.current.voiceChat.stop()
        await sessionRef.current.stop()
        sessionRef.current.removeAllListeners()
      } catch (error) {
        console.remote('useLiveAvatar: error ending session:', error)
      } finally {
        sessionRef.current = null
      }
    }

    // Clean up video element
    if (mediaStream.current && mediaStream.current.srcObject) {
      const srcStream = mediaStream.current.srcObject as MediaStream
      srcStream.getTracks().forEach((track) => {
        console.remote('useLiveAvatar: stopping track:', track.kind, track.label)
        track.stop()
      })
      mediaStream.current.srcObject = null
    }

    // Reset state
    setStream(undefined)
    setIsLoadingSession(false)
    setUserMediaStream(null)
  }, [isListening])

  const handleSpeak = useCallback(async (text: string) => {
    if (!sessionRef.current) return
    setIsLoadingRepeat(true)
    try {
      await sessionRef.current.repeat(text)
    } catch (error) {
      console.remote('useLiveAvatar: error speaking:', error)
    } finally {
      setIsLoadingRepeat(false)
    }
  }, [])

  const handleInterrupt = useCallback(async () => {
    if (!sessionRef.current) return
    try {
      await sessionRef.current.interrupt()
    } catch (error) {
      console.remote('useLiveAvatar: error interrupting:', error)
    }
  }, [])

  const startListening = useCallback(async () => {
    if (!sessionRef.current || isListening) return
    try {
      await sessionRef.current.startListening()
      setIsListening(true)
      console.remote('useLiveAvatar: started listening')
    } catch (error) {
      console.remote('useLiveAvatar: error starting listening:', error)
    }
  }, [isListening])

  const stopListening = useCallback(async () => {
    if (!sessionRef.current || !isListening) return
    try {
      await sessionRef.current.stopListening()
      setIsListening(false)
      console.remote('useLiveAvatar: stopped listening')
    } catch (error) {
      console.remote('useLiveAvatar: error stopping listening:', error)
    }
  }, [isListening])

  const toggleListening = useCallback(async () => {
    if (isListening) {
      await stopListening()
    } else {
      await startListening()
    }
  }, [isListening, startListening, stopListening])

  // Attach video element when stream becomes ready
  useEffect(() => {
    if (stream && sessionRef.current && mediaStream.current) {
      console.remote('useLiveAvatar: useEffect attaching video')
      sessionRef.current.attach(mediaStream.current)
      mediaStream.current.play()
    }
  }, [stream])

  return {
    startSession,
    endSession,
    mediaStream,
    isLoadingSession,
    isLoadingRepeat,
    handleSpeak,
    handleInterrupt,
    stream,
    isUserTalking,
    isListening,
    startListening,
    stopListening,
    toggleListening,
    userMediaStream,
    // Backward compatibility props
    setText: () => {},
    data: undefined,
    text: '',
  }
}

export default useLiveAvatar
